import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useCurrentApp } from '../../../context/CurrentApp';
import { polygonService } from '../../../services/central-api/polygons';
import { translate } from '../../../services/translate';
import FilterRadioList, { FilterListItem } from './FilterRadioList';
import FilterBadgeSearchList from './FilterBadgeSearchList';

interface Props {
    polygonType: string;
    setPolygonType: React.Dispatch<React.SetStateAction<string>>;
    mapPolygonsIds: number[];
    setMapPolygonsIds: React.Dispatch<React.SetStateAction<number[]>>;
    setPendingLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    companyId?: number;
    requiresCompanyId?: boolean;
}

export default function DispatchRegionsList({
    polygonType,
    setPolygonType,
    mapPolygonsIds,
    setMapPolygonsIds,
    setPendingLoading,
    companyId,
    requiresCompanyId,
}: Props) {
    const { styles } = useStyles(styleSheet);
    const { isParentApp } = useCurrentApp();

    const [typeItems, setTypeItems] = useState<FilterListItem<string>[]>([]);
    const [typeIsLoading, setTypeIsLoading] = useState(true);

    const [polygonItems, setPolygonItems] = useState<FilterListItem[]>([]);
    const [polygonsIsLoading, setPolygonsIsLoading] = useState(false);

    async function getPolygonsType() {
        try {
            setTypeIsLoading(true);
            const polygonsType = await polygonService.getPolygonsType(
                isParentApp && companyId && requiresCompanyId ? companyId : undefined
            );
            setTypeItems(polygonsType.map((polygon) => ({
                label: polygon.type,
                value: polygon.type
            })));
        } catch (err) {
            console.error(err);
        } finally {
            setTypeIsLoading(false);
        }
    }

    async function getPolygonsByType(polygonType: string) {
        try {
            setPendingLoading?.(true);
            setPolygonsIsLoading(true);
            const resp = await polygonService.getPolygonsByType(
                polygonType,
                isParentApp && companyId && requiresCompanyId ? companyId : undefined
            );
            setPolygonItems(resp.map((polygon) => ({ label: polygon.name, value: polygon.id })));
        } catch (err) {
            console.error(err);
        } finally {
            setPendingLoading?.(false);
            setPolygonsIsLoading(false);
        }
    }

    useEffect(() => {
        if (requiresCompanyId && isParentApp && !companyId) {
            setTypeIsLoading(false);
            return;
        }
        getPolygonsType();
    }, [isParentApp, companyId]);

    useEffect(() => {
        if ((requiresCompanyId && isParentApp && !companyId) || !polygonType) {
            setPolygonItems([]);
            return;
        }
        setPolygonItems([]);
        getPolygonsByType(polygonType);
    }, [polygonType, isParentApp, companyId]);

    return (
        <View style={styles.container}>
            <FilterRadioList
                label={translate('regions')}
                items={typeItems}
                value={polygonType}
                setValue={(value) => {
                    const resolved = typeof value === 'function' ? value(polygonType) : value;
                    setMapPolygonsIds([]);
                    setPolygonType(resolved as string);
                }}
                clearValue=''
                isLoading={typeIsLoading}
            />

            {polygonType !== '' && (
                <View style={styles.subRegionsContainer}>
                    <FilterBadgeSearchList
                        label={translate('subRegions')}
                        items={polygonItems}
                        value={mapPolygonsIds}
                        setValue={setMapPolygonsIds}
                        isLoading={polygonsIsLoading}
                    />
                </View>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet(() => ({
    container: {
        gap: 20,
    },
    subRegionsContainer: {
        paddingLeft: 16,
    },
}));
